/* eslint-disable unicorn/prefer-module */
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { Loader } from "..";
import { getEdificiosOperativosPublico, useCategoriaMoneda } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaMascotas.module.scss";

// =====================================================
// DATOS IPSOS MASCOTAS (Base: 400 casos)
// =====================================================
const IPSOS_MASCOTAS = {
  penetracion: 0.616, // 61.6% tienen mascotas
  noPenetracion: 0.384, // 38.4% no tienen mascotas
  cantidadMascotas: {
    una: 0.472, // 47.2%
    dos: 0.285, // 28.5%
    tres: 0.094, // 9.4%
    cuatroMas: 0.149, // 14.9%
  },
  // Promedio ponderado de mascotas
  promedioMascotas: 0.472 * 1 + 0.285 * 2 + 0.094 * 3 + 0.149 * 4.5, // ~1.92
  tipoMascota: {
    perro: 0.711, // 71.1%
    gato: 0.42, // 42%
    caballo: 0.014, // 1.4%
    conejo: 0.009, // 0.9%
    otros: 0.057, // otros (~5.7%)
  },
  // Importancia de la mascota
  importancia: {
    muyImportante: 0.879, // 87.9%
    bastante: 0.107, // 10.7%
    algo: 0.014, // 1.4%
  },
  // Frecuencia compra alimento
  frecuenciaCompra: {
    semanal: { pct: 0.047, factor: 4 }, // 4.7%
    cadaDosSemannas: { pct: 0.038, factor: 2 }, // 3.8%
    mensual: { pct: 0.728, factor: 1 }, // 72.8%
    cadaDosMeses: { pct: 0.186, factor: 0.5 }, // 18.6%
  },
  // Dónde compran
  canalCompra: {
    "Tienda de mascotas": 0.384,
    Supermercado: 0.229,
    Internet: 0.156,
    Distribuidora: 0.069,
    Veterinaria: 0.05,
    Otros: 0.112,
  },
  // Market share marcas
  marketSharePerros: {
    "Royal Canin": 0.112,
    "Fórmula Fit": 0.089,
    Purina: 0.068,
    Pedigree: 0.068,
    "Brit Care": 0.062,
    Eukanuba: 0.055,
    "Pro Plan": 0.048,
    "Hills Science": 0.042,
    "Dog Chow": 0.038,
    Otras: 0.418,
  },
  marketShareGatos: {
    "Royal Canin": 0.145,
    Whiskas: 0.098,
    Felix: 0.085,
    "Cat Chow": 0.072,
    "Pro Plan": 0.065,
    "Hills Science": 0.058,
    Otras: 0.477,
  },
};

// Consumo mensual por tamaño de perro (kg de alimento)
const CONSUMO_PERROS = {
  grande: { peso: "25-35 kg", consumoDiario: 0.5, consumoMensual: 15, descripcion: "Labrador, Pastor Alemán" },
  mediano: { peso: "10-25 kg", consumoDiario: 0.3, consumoMensual: 9, descripcion: "Beagle, Cocker" },
  chico: { peso: "< 10 kg", consumoDiario: 0.17, consumoMensual: 5, descripcion: "Chihuahua, Yorkshire" },
};

// Consumo mensual gatos (kg de alimento)
const CONSUMO_GATOS = {
  adulto: { peso: "3-5 kg", consumoDiario: 0.06, consumoMensual: 1.8, descripcion: "Gato adulto promedio" },
};

// Precios de referencia por kg (CLP) - Basado en Royal Canin ~$4.500/kg, Pro Plan ~$5.200/kg
const PRECIOS_ALIMENTO_DEFAULT = {
  perros: {
    superpremium: { nombre: "Super Premium", precioKg: 5000 },
    premium: { nombre: "Premium", precioKg: 3500 },
    estandar: { nombre: "Estándar", precioKg: 2000 },
  },
  gatos: {
    superpremium: { nombre: "Super Premium", precioKg: 6000 },
    premium: { nombre: "Premium", precioKg: 4000 },
    estandar: { nombre: "Estándar", precioKg: 2500 },
  },
};

// Escenarios de captación
const ESCENARIOS_CAPTACION = [2, 4, 6, 8, 10];

export const Mascotas = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const [edificios, setEdificios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkAll, setCheckAll] = useState(true);
  const [checkedBuildings, setCheckedBuildings] = useState({});
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [vistaActiva, setVistaActiva] = useState("perros"); // 'perros' o 'gatos'
  const [verAnual, setVerAnual] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);

  // Estado para simulador
  const [calidadAlimento, setCalidadAlimento] = useState("superpremium");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(4);
  const [marcaSeleccionada, setMarcaSeleccionada] = useState("Royal Canin");

  // Estado para distribución de mercado por segmento (debe sumar 100%)
  const [distribucionMercado, setDistribucionMercado] = useState({
    superpremium: 20,
    premium: 35,
    estandar: 45,
  });

  // Función para actualizar distribución manteniendo suma = 100%
  // Los valores deben ser múltiplos de 5 para coincidir con las opciones del select
  const actualizarDistribucion = useCallback((segmento, nuevoValor) => {
    setDistribucionMercado((prev) => {
      const segmentos = ["superpremium", "premium", "estandar"];
      const otros = segmentos.filter((k) => k !== segmento);
      const sumaOtros = otros.reduce((sum, k) => sum + prev[k], 0);
      const disponible = 100 - nuevoValor;

      // Redondear a múltiplo de 5 más cercano
      const redondearA5 = (n) => Math.round(n / 5) * 5;

      if (sumaOtros === 0) {
        // Si los otros son 0, distribuir equitativamente
        const valorPrimero = redondearA5(disponible / 2);
        return {
          [segmento]: nuevoValor,
          [otros[0]]: valorPrimero,
          [otros[1]]: disponible - valorPrimero,
        };
      }

      // Distribuir proporcionalmente entre los otros (redondeando a múltiplos de 5)
      const proporcion0 = prev[otros[0]] / sumaOtros;
      const valor0 = redondearA5(disponible * proporcion0);
      const valor1 = disponible - valor0;

      return {
        [segmento]: nuevoValor,
        [otros[0]]: valor0,
        [otros[1]]: valor1,
      };
    });
  }, []);

  useEffect(() => {
    const initialize = async () => {
      try {
        const newEdificios = await getEdificiosOperativosPublico({ pais });
        setEdificios(newEdificios);

        const initialChecked = {};
        newEdificios.forEach(({ id }) => {
          initialChecked[id] = true;
        });
        setCheckedBuildings(initialChecked);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [pais]);

  const toggleCheckAll = useCallback(() => {
    if (!checkAll) {
      const allChecked = {};
      edificios.forEach(({ id }) => {
        allChecked[id] = true;
      });
      setCheckedBuildings(allChecked);
    } else {
      setCheckedBuildings({});
    }
    setCheckAll((prev) => !prev);
  }, [checkAll, edificios]);

  const toggleCheck = useCallback((id) => {
    setCheckedBuildings((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // Calcular totales de edificios seleccionados
  const { totalEjecutivos, cantidadEdificios, totalPantallas, totalEmpresas } = useMemo(() => {
    let ejecutivos = 0;
    let cantidad = 0;
    let pantallas = 0;
    let empresas = 0;

    edificios.forEach(({ id, personas, pantallas: p, espera, totems, cowork, empresaslistado }) => {
      if (checkedBuildings[id]) {
        cantidad++;
        ejecutivos += personas || 0;
        pantallas += (p || 0) + (espera || 0) + (totems || 0) + (cowork || 0);
        empresas += empresaslistado ? empresaslistado.split(",").length : 0;
      }
    });

    return {
      totalEjecutivos: ejecutivos,
      cantidadEdificios: cantidad,
      totalPantallas: pantallas,
      totalEmpresas: empresas,
    };
  }, [edificios, checkedBuildings]);

  // =====================================================
  // CÁLCULOS GENERALES MASCOTAS
  // =====================================================
  const resultadosBase = useMemo(() => {
    if (!totalEjecutivos) return null;

    const tienenMascotas = Math.round(totalEjecutivos * IPSOS_MASCOTAS.penetracion);
    const noTienen = Math.round(totalEjecutivos * IPSOS_MASCOTAS.noPenetracion);

    // Cantidad de mascotas
    const conUna = Math.round(tienenMascotas * IPSOS_MASCOTAS.cantidadMascotas.una);
    const conDos = Math.round(tienenMascotas * IPSOS_MASCOTAS.cantidadMascotas.dos);
    const conTres = Math.round(tienenMascotas * IPSOS_MASCOTAS.cantidadMascotas.tres);
    const conCuatroMas = Math.round(tienenMascotas * IPSOS_MASCOTAS.cantidadMascotas.cuatroMas);
    const totalMascotas = conUna * 1 + conDos * 2 + conTres * 3 + conCuatroMas * 4.5;

    // Tipos de mascota
    const tienenPerro = Math.round(tienenMascotas * IPSOS_MASCOTAS.tipoMascota.perro);
    const tienenGato = Math.round(tienenMascotas * IPSOS_MASCOTAS.tipoMascota.gato);

    // Estimación de número de perros y gatos
    // Asumimos distribución proporcional
    const totalPerros = Math.round(totalMascotas * 0.6); // ~60% son perros
    const totalGatos = Math.round(totalMascotas * 0.35); // ~35% son gatos

    return {
      tienenMascotas,
      noTienen,
      cantidadMascotas: {
        una: conUna,
        dos: conDos,
        tres: conTres,
        cuatroMas: conCuatroMas,
      },
      totalMascotas: Math.round(totalMascotas),
      tienenPerro,
      tienenGato,
      totalPerros,
      totalGatos,
    };
  }, [totalEjecutivos]);

  // =====================================================
  // CÁLCULOS PERROS
  // =====================================================
  const resultadosPerros = useMemo(() => {
    if (!resultadosBase) return null;

    const { tienenPerro, totalPerros } = resultadosBase;

    // Distribución por tamaño (estimada)
    const distribucionTamano = {
      grande: 0.25, // 25% perros grandes
      mediano: 0.45, // 45% perros medianos
      chico: 0.3, // 30% perros chicos
    };

    const perrosPorTamano = {};
    let consumoTotalKgMes = 0;

    Object.entries(distribucionTamano).forEach(([tamano, pct]) => {
      const cantidad = Math.round(totalPerros * pct);
      const consumoMes = cantidad * CONSUMO_PERROS[tamano].consumoMensual;
      perrosPorTamano[tamano] = {
        cantidad,
        consumoMes,
        consumoAnual: consumoMes * 12,
      };
      consumoTotalKgMes += consumoMes;
    });

    // Calcular mercado por calidad de alimento (usando distribución de mercado)
    const mercadoPorCalidad = {};
    Object.entries(PRECIOS_ALIMENTO_DEFAULT.perros).forEach(([calidad, data]) => {
      const kgSegmento = consumoTotalKgMes * (distribucionMercado[calidad] / 100);
      mercadoPorCalidad[calidad] = {
        nombre: data.nombre,
        precioKg: data.precioKg,
        kgMensual: kgSegmento,
        mercadoMensual: kgSegmento * data.precioKg,
        mercadoAnual: kgSegmento * data.precioKg * 12,
      };
    });

    // Market share marcas
    const marketShareData = {};
    // Mercado promedio (usando superpremium como referencia)
    const mercadoReferenciaMsg = consumoTotalKgMes * PRECIOS_ALIMENTO_DEFAULT.perros.superpremium.precioKg;
    Object.entries(IPSOS_MASCOTAS.marketSharePerros).forEach(([marca, share]) => {
      marketShareData[marca] = {
        share,
        mercadoMensual: Math.round(mercadoReferenciaMsg * share),
      };
    });

    return {
      tienenPerro,
      totalPerros,
      perrosPorTamano,
      consumoTotalKgMes,
      consumoTotalKgAnual: consumoTotalKgMes * 12,
      mercadoPorCalidad,
      marketShareData,
    };
  }, [resultadosBase, distribucionMercado]);

  // =====================================================
  // CÁLCULOS GATOS
  // =====================================================
  const resultadosGatos = useMemo(() => {
    if (!resultadosBase) return null;

    const { tienenGato, totalGatos } = resultadosBase;

    const consumoMes = totalGatos * CONSUMO_GATOS.adulto.consumoMensual;

    // Calcular mercado por calidad de alimento (usando distribución de mercado)
    const mercadoPorCalidad = {};
    Object.entries(PRECIOS_ALIMENTO_DEFAULT.gatos).forEach(([calidad, data]) => {
      const kgSegmento = consumoMes * (distribucionMercado[calidad] / 100);
      mercadoPorCalidad[calidad] = {
        nombre: data.nombre,
        precioKg: data.precioKg,
        kgMensual: kgSegmento,
        mercadoMensual: kgSegmento * data.precioKg,
        mercadoAnual: kgSegmento * data.precioKg * 12,
      };
    });

    // Market share marcas
    const marketShareData = {};
    const mercadoReferenciaMsg = consumoMes * PRECIOS_ALIMENTO_DEFAULT.gatos.superpremium.precioKg;
    Object.entries(IPSOS_MASCOTAS.marketShareGatos).forEach(([marca, share]) => {
      marketShareData[marca] = {
        share,
        mercadoMensual: Math.round(mercadoReferenciaMsg * share),
      };
    });

    return {
      tienenGato,
      totalGatos,
      consumoMes,
      consumoAnual: consumoMes * 12,
      mercadoPorCalidad,
      marketShareData,
    };
  }, [resultadosBase, distribucionMercado]);

  // =====================================================
  // CÁLCULO OPORTUNIDAD
  // =====================================================
  const calcularOportunidad = useCallback(() => {
    if (vistaActiva === "perros" && resultadosPerros) {
      const mercadoBase = resultadosPerros.mercadoPorCalidad[calidadAlimento];
      if (!mercadoBase) return null;

      const mercadoCaptadoMensual = Math.round(mercadoBase.mercadoMensual * (porcentajeCaptacion / 100));

      return {
        mercadoTotal: mercadoBase.mercadoMensual,
        mercadoCaptadoMensual,
        mercadoCaptadoAnual: mercadoCaptadoMensual * 12,
        kgMensuales: Math.round(resultadosPerros.consumoTotalKgMes * (porcentajeCaptacion / 100)),
      };
    } else if (vistaActiva === "gatos" && resultadosGatos) {
      const mercadoBase = resultadosGatos.mercadoPorCalidad[calidadAlimento];
      if (!mercadoBase) return null;

      const mercadoCaptadoMensual = Math.round(mercadoBase.mercadoMensual * (porcentajeCaptacion / 100));

      return {
        mercadoTotal: mercadoBase.mercadoMensual,
        mercadoCaptadoMensual,
        mercadoCaptadoAnual: mercadoCaptadoMensual * 12,
        kgMensuales: Math.round(resultadosGatos.consumoMes * (porcentajeCaptacion / 100)),
      };
    }
    return null;
  }, [vistaActiva, resultadosPerros, resultadosGatos, calidadAlimento, porcentajeCaptacion]);

  if (loading || edificios.length === 0) return <Loader />;

  return (
    <div className={styles.mascotasPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>ALIMENTO MASCOTAS</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <div className={styles.mascotasContent}>
        {/* Resumen de penetración */}
        {resultadosBase && (
          <div className={styles.resumenPenetracion}>
            <div className={styles.resumenCard}>
              <div className={styles.resumenValor}>
                <span className={styles.valorDestacado}>{formatNumber(resultadosBase.tienenMascotas)}</span>
              </div>
              <div className={styles.resumenLabel}>Tienen mascotas</div>
              <div className={styles.resumenDetalle}>
                <span className={styles.badgeIpsos}>{(IPSOS_MASCOTAS.penetracion * 100).toFixed(0)}%</span> de
                ejecutivos
              </div>
            </div>
            <div className={styles.resumenCard}>
              <div className={styles.resumenValor}>
                <span className={styles.valorDestacado}>{formatNumber(resultadosBase.totalMascotas)}</span>
                <span className={styles.tooltipContainer}>
                  <FaInfoCircle className={styles.infoIcon} />
                  <span className={styles.tooltipText}>
                    Promedio ~2 mascotas por hogar (47% tiene 1, 29% tiene 2, 9% tiene 3, 15% tiene 4+). Distribución
                    estimada: 60% perros, 35% gatos, 5% otros.
                  </span>
                </span>
              </div>
              <div className={styles.resumenLabel}>Total mascotas estimadas</div>
              <div className={styles.resumenDetalle}>
                {formatNumber(resultadosBase.totalPerros)} perros | {formatNumber(resultadosBase.totalGatos)} gatos
              </div>
            </div>
            <div className={styles.resumenCard}>
              <div className={styles.resumenValor}>
                <span className={styles.badgeIpsosGrande}>
                  {(IPSOS_MASCOTAS.importancia.muyImportante * 100).toFixed(0)}%
                </span>
              </div>
              <div className={styles.resumenLabel}>Considera muy importante</div>
              <div className={styles.resumenLabel}>su mascota en la familia</div>
            </div>
          </div>
        )}

        {/* Tabs para cambiar entre Perros y Gatos */}
        <div className={styles.tabsContainer}>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "perros" && styles.tabActivo)}
            onClick={() => setVistaActiva("perros")}
          >
            Perros
          </button>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "gatos" && styles.tabActivo)}
            onClick={() => setVistaActiva("gatos")}
          >
            Gatos
          </button>
        </div>

        {/* =====================================================
            VISTA PERROS
            ===================================================== */}
        {vistaActiva === "perros" && resultadosPerros && (
          <>
            {/* Info general */}
            <div className={styles.infoSection}>
              <div className={styles.infoRow}>
                <span className={styles.badgeIpsos}>{(IPSOS_MASCOTAS.tipoMascota.perro * 100).toFixed(0)}%</span>
                <span>
                  de quienes tienen mascotas tienen al menos un perro → al menos{" "}
                  <strong>{formatNumber(resultadosPerros.tienenPerro)}</strong> dueños de perros (
                  {formatNumber(resultadosPerros.totalPerros)} perros estimados)
                </span>
              </div>
            </div>

            {/* Tabla de consumo por tamaño */}
            <div className={styles.tablaContainer}>
              <h4 className={styles.subtitulo}>
                Consumo de Alimento por Tamaño
                <span className={styles.tooltipContainer}>
                  <FaInfoCircle className={styles.infoIcon} />
                  <span className={styles.tooltipTextWide}>
                    <strong>Distribución por tamaño</strong> (estimación estándar de mercado):
                    <br />• Grande (25%): razas tipo Labrador, Pastor Alemán
                    <br />• Mediano (45%): razas tipo Beagle, Cocker
                    <br />• Chico (30%): razas tipo Chihuahua, Yorkshire
                    <br />
                    <br />
                    <strong>Consumo mensual</strong> (recomendaciones fabricantes):
                    <br />• Grande (25-35 kg): ~15 kg/mes
                    <br />• Mediano (10-25 kg): ~9 kg/mes
                    <br />• Chico (&lt;10 kg): ~5 kg/mes
                    <br />
                    <br />
                    <strong>Cálculo:</strong> Kg totales = Cantidad × Consumo mensual
                  </span>
                </span>
              </h4>
              <table className={cx("table table-sm", styles.tablaResultados)}>
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">Tamaño</th>
                    <th className="text-center">Peso mascota</th>
                    <th className="text-center">Cantidad perros</th>
                    <th className="text-center">Consumo/mes (kg)</th>
                    <th className="text-center">Kg/mes</th>
                    <th className="text-center">Kg/año</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(CONSUMO_PERROS).map(([tamano, data]) => {
                    const resultado = resultadosPerros.perrosPorTamano[tamano];
                    return (
                      <tr key={tamano}>
                        <td className="text-center text-capitalize">
                          <strong>{tamano}</strong>
                        </td>
                        <td className="text-center">{data.peso}</td>
                        <td className="text-center">{formatNumber(resultado.cantidad)}</td>
                        <td className="text-center">{data.consumoMensual}</td>
                        <td className="text-center">
                          <strong>{formatNumber(resultado.consumoMes)}</strong>
                        </td>
                        <td className="text-center">
                          <strong>{formatNumber(resultado.consumoAnual)}</strong>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className={styles.filaTotales}>
                    <td colSpan={4} />
                    <td className={cx("text-center", styles.celdaTotal)}>
                      <strong>{formatNumber(resultadosPerros.consumoTotalKgMes)}</strong>
                    </td>
                    <td className={cx("text-center", styles.celdaTotal)}>
                      <strong>{formatNumber(resultadosPerros.consumoTotalKgAnual)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mercado por segmento */}
            <div className={styles.mercadoCalidadContainer}>
              <div className={styles.mercadoHeader}>
                <h4 className={styles.subtitulo}>Mercado por Segmento</h4>
                <div className={styles.mercadoToggle}>
                  <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>Mensual</span>
                  <label className={styles.toggleSwitch}>
                    <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                    <span className={styles.toggleSlider} />
                  </label>
                  <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>Anual</span>
                </div>
                <span className={styles.mercadoTotalValor}>
                  {formatLocal(
                    convertCLP(
                      Object.values(resultadosPerros.mercadoPorCalidad).reduce(
                        (sum, d) => sum + (verAnual ? d.mercadoAnual : d.mercadoMensual),
                        0
                      )
                    )
                  )}
                </span>
              </div>
              <div className={styles.mercadoCalidadGrid}>
                {Object.entries(resultadosPerros.mercadoPorCalidad).map(([calidad, data]) => (
                  <div key={calidad} className={styles.mercadoCalidadCard}>
                    <div className={styles.distribucionSelect}>
                      <select
                        value={distribucionMercado[calidad]}
                        onChange={(e) => actualizarDistribucion(calidad, Number(e.target.value))}
                        className={styles.selectDistribucion}
                      >
                        {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100].map(
                          (pct) => (
                            <option key={pct} value={pct}>
                              {pct}%
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className={styles.calidadNombre}>{data.nombre}</div>
                    <div className={styles.calidadPrecio}>{formatLocal(convertCLP(data.precioKg))}/kg</div>
                    <div className={styles.calidadMercado}>
                      <span className={styles.mercadoLabel}>Mercado:</span>
                      <span className={styles.mercadoValor}>
                        {formatLocal(convertCLP(verAnual ? data.mercadoAnual : data.mercadoMensual))}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Market Share + Canales de Compra */}
            <div className={styles.shareCanalesRow}>
              {/* Market Share - 70% */}
              <div className={styles.marketShareSection}>
                <h4 className={styles.subtitulo}>Market Share - Perros</h4>
                <div className={styles.marketShareGrid}>
                  {Object.entries(resultadosPerros.marketShareData)
                    .slice(0, 9)
                    .map(([nombre, data]) => (
                      <div key={nombre} className={styles.marketShareCard}>
                        <span className={styles.cardNombre}>{nombre}</span>
                        <span className={styles.badgeIpsos}>{(data.share * 100).toFixed(0)}%</span>
                        <span className={styles.cardMercado}>
                          {formatLocal(convertCLP(verAnual ? data.mercadoMensual * 12 : data.mercadoMensual))}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Canales de Compra - 30% */}
              <div className={styles.canalesSection}>
                <h4 className={styles.subtitulo}>Canales de Compra</h4>
                <div className={styles.canalesChart}>
                  {Object.entries(IPSOS_MASCOTAS.canalCompra).map(([canal, pct]) => (
                    <div key={canal} className={styles.canalBarItem}>
                      <div className={styles.canalBarLabel}>
                        <span>{canal}</span>
                        <span className={styles.canalBarPct}>{(pct * 100).toFixed(0)}%</span>
                      </div>
                      <div className={styles.canalBarTrack}>
                        <div className={styles.canalBarFill} style={{ width: `${pct * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
              Simular Oportunidad
            </button>

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <p>
                <strong>Fuente:</strong> IPSOS Wecast 2025. Base: 400 casos / 248 con mascotas. El 71.1% de quienes
                tienen mascotas tienen perro. Frecuencia de compra: 72.8% compra alimento mensualmente. Distribución por
                tamaño estimada en base a estudios del mercado chileno.
              </p>
            </div>
          </>
        )}

        {/* =====================================================
            VISTA GATOS - EN CONSTRUCCIÓN
            ===================================================== */}
        {vistaActiva === "gatos" && (
          <div className={styles.enConstruccion}>
            <span className={styles.enConstruccionIcono}>🚧</span>
            <h4>Sección en construcción</h4>
            <p>El análisis de mercado de alimento para gatos estará disponible próximamente.</p>
          </div>
        )}
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> ejecutivos
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad */}
      {showOportunidadModal && (resultadosPerros || resultadosGatos) && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad - Alimento para {vistaActiva === "perros" ? "Perros" : "Gatos"}</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.simuladorContainer}>
                <div className={styles.simuladorControles}>
                  {vistaActiva === "perros" && (
                    <div className={styles.controlGroup}>
                      <label>Marca objetivo:</label>
                      <select
                        value={marcaSeleccionada}
                        onChange={(e) => setMarcaSeleccionada(e.target.value)}
                        className="form-select form-select-sm"
                      >
                        {Object.keys(IPSOS_MASCOTAS.marketSharePerros).map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {vistaActiva === "gatos" && (
                    <div className={styles.controlGroup}>
                      <label>Marca objetivo:</label>
                      <select
                        value={marcaSeleccionada}
                        onChange={(e) => setMarcaSeleccionada(e.target.value)}
                        className="form-select form-select-sm"
                      >
                        {Object.keys(IPSOS_MASCOTAS.marketShareGatos).map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className={styles.controlGroup}>
                    <label>Calidad alimento:</label>
                    <select
                      value={calidadAlimento}
                      onChange={(e) => setCalidadAlimento(e.target.value)}
                      className="form-select form-select-sm"
                    >
                      <option value="premium">Premium</option>
                      <option value="superpremium">Super Premium</option>
                      <option value="estandar">Estándar</option>
                    </select>
                  </div>
                  <div className={styles.controlGroup}>
                    <label>% captación mercado:</label>
                    <select
                      value={porcentajeCaptacion}
                      onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                      className="form-select form-select-sm"
                    >
                      {ESCENARIOS_CAPTACION.map((p) => (
                        <option key={p} value={p}>
                          {p}%
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {(() => {
                  const oportunidad = calcularOportunidad();
                  if (!oportunidad) return null;

                  return (
                    <div className={styles.simuladorResultados}>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado total mensual:</span>
                        <span className={styles.resultadoValor}>
                          {formatLocal(convertCLP(oportunidad.mercadoTotal))}
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Kg a captar/mes:</span>
                        <span className={styles.resultadoValor}>{formatNumber(oportunidad.kgMensuales)} kg</span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Captación al {porcentajeCaptacion}%:</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.mercadoCaptadoMensual))}/mes
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado captado anual:</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.mercadoCaptadoAnual))}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mascotas;
